import ModalErrorBoundary from '@UI/modals/ModalErrorBoundary';
import ZillCurriculumSelectorModal from '@UI/modals/ZillCurriculumSelectorModal';
import type { ContentHref } from '@generalTypes/apiTypes';
import { Modal } from '@kathondvla/react-shared-components/src/components';
import { getAngularService } from '@kathondvla/react-shared-components/src/helpers/angularReactHelper';
import { getNow } from '@kathondvla/sri-client/date-utils';
import {
  buildCurrentSelectionForZillSelector,
  groupGoalsByZillCurriculum,
} from '@newStore/zill/zillHelpers';
import { processNewSelectedIllustrations } from '@newStore/zill/zillSagas';
import { selectLinkedZillGoalsForPracticalExample } from '@newStore/zill/zillSelectors';
import type { EditAsideZillIllustrations } from '@nodeTypeConfig/configTypes';
import {
  addZillIllustrationAction,
  removeZillIllustrationRelationAction,
} from '@store/actions/documentActions';
import { getResourceKey } from '@store/helpers/documentHelpers';
import { any } from 'prop-types';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useEditHref } from '../../../hooks/UseEditHref';
import { useTypedSelector } from '../../../hooks/useTypedSelector';
import ZillIllustrationModal from '@UI/modals/ZillIllustrationModal';
import { setNewPendingZillIllustrations } from '@newStore/documentUI/documentUIState';

const AsideZillIllustrations: React.FC<{
  config: EditAsideZillIllustrations;
  readonly: boolean;
}> = ({ config, readonly }) => {
  const dispatch = useDispatch();
  const editHref = useEditHref();
  const editKey = getResourceKey(editHref);
  const curriculumModalRef = useRef<Modal>();
  const illustrationTextModalRef = useRef<Modal>();
  const confirmationService = getAngularService('ConfirmationService');
  const zillSelectorService = getAngularService('ZillSelectorService');

  const { illustrationsAreLoaded, genericGoals, loadingZillGoals } = useTypedSelector((state) =>
    selectLinkedZillGoalsForPracticalExample(state),
  );
  const loading = !illustrationsAreLoaded || loadingZillGoals.length > 0;

  const goalsGroupedByZillCurriculum = groupGoalsByZillCurriculum(genericGoals);
  const zillCurriculumGroups = Object.values(goalsGroupedByZillCurriculum);

  /* Open modal to enter title when a new illustration is added from the zill selector */
  const newPendingZillIllustrations = useTypedSelector(
    (state) => state.documentUI.newPendingZillIllustrations,
  );

  useEffect(() => {
    if (newPendingZillIllustrations.length > 0) {
      const { goal, subDetail } = newPendingZillIllustrations[0];
      illustrationTextModalRef.current?.open({
        goal,
        subDetail,
        goalHref: goal.href,
        onSubmit: (illustrationText) => {
          dispatch(
            addZillIllustrationAction(
              editKey,
              { title: illustrationText },
              subDetail.length ? subDetail.map((dc) => dc.href) : [goal.href],
            ),
          );
          dispatch(setNewPendingZillIllustrations(newPendingZillIllustrations.slice(1)));
        },
      });
    }
  }, [newPendingZillIllustrations, editKey, dispatch]);

  const selectZillVersion = async () => {
    if (readonly) {
      return;
    }

    curriculumModalRef.current?.open({
      onSubmit: openSelector,
    });
  };

  const openSelector = async (zillCurriculum) => {
    try {
      // after selecting a zill version, we will open the zill selector where you can adapt the
      // selected goals of that version. We do not touch the goals of different zill versions.

      const groupForSelectedVersion = goalsGroupedByZillCurriculum[zillCurriculum.$$meta.permalink];
      const currentSelections = groupForSelectedVersion
        ? buildCurrentSelectionForZillSelector(groupForSelectedVersion.goals)
        : [];

      const selectedGoals: ContentHref[] = await zillSelectorService.openZillSelector(
        zillCurriculum.$$meta.permalink,
        currentSelections.map((sel) => sel.href),
        'praktijkvoorbeeld',
      );

      dispatch(
        processNewSelectedIllustrations({
          selectedGoals,
          currentSelections,
          genericGoals,
        }),
      );
    } catch (error) {
      if (error === 'ZILL_SELECTOR_ABORTED') {
        console.log('The user closed the zill selector manually.');
      } else {
        console.error(
          'An unexpected error occured when communicating with the Zill selector',
          error,
        );
      }
    }
  };

  const deleteIllustration = async (goal) => {
    if (readonly) {
      return;
    }
    // delete illustration and relation to document
    const confirmed = await confirmationService.confirmDeletion();
    if (confirmed) {
      dispatch(
        removeZillIllustrationRelationAction(
          getResourceKey(goal.illustration.href),
          getResourceKey(goal.illustration.relationToPracticalExampleHref),
        ),
      );
    }
  };

  return (
    <div>
      <div className="panel panel-default">
        <div className="panel-heading">
          <div className="panel-controls panel-controls-left">
            <h3 className="panel-title">{config.label}</h3>
          </div>
          <div className="panel-controls panel-controls-right">
            <div className="btn-toolbar">
              {illustrationsAreLoaded && (
                <button className="btn btn-default" onClick={selectZillVersion}>
                  <span>
                    <img src="/images/logo_zill.png" width="20px" />
                  </span>
                  <span>selector</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {loading && <div className="shimmer narrowLine" />}

        {!loading && zillCurriculumGroups.length > 0 && (
          <div className="structure">
            {zillCurriculumGroups.map((row) => (
              <div key={row.href}>
                <div className="row table-row">
                  <div className="col-md-12" style={{ marginBottom: '-5px', marginTop: '15px' }}>
                    <span>
                      <small>
                        <strong>{row.title}</strong>
                      </small>
                    </span>
                  </div>
                </div>

                {row.goals.map((goal) => (
                  <div className="row table-row" key={goal.href}>
                    <div className="col-md-12" style={{ marginLeft: '15px' }}>
                      <div style={{ color: goal.color }}>
                        <small>
                          <span style={{ fontWeight: 'bold' }}>{goal.code}</span>:
                          <span style={{ fontWeight: 'bold' }}>{goal.title}</span>
                          {goal.subDetails.map((subDetail) => (
                            <span key={subDetail.href}>
                              <br />
                              <span
                                style={{ marginLeft: '10px' }}
                                title="{{subDetail.description}}"
                              >
                                -&gt; {subDetail.shortDescription}
                              </span>
                            </span>
                          ))}
                        </small>
                      </div>
                      {goal?.illustration.title ? (
                        <div dangerouslySetInnerHTML={{ __html: goal.illustration.title }} />
                      ) : (
                        <div style={{ color: 'lightgray' }}>[illustratie zonder titel]</div>
                      )}
                    </div>

                    <div
                      className="btn-group btn-group-xs row-actions"
                      role="group"
                      aria-label="..."
                    >
                      <a
                        className="btn btn-default"
                        href={`/edit/${getResourceKey(goal.illustration.href)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span className="glyphicon glyphicon-edit" />
                      </a>
                      <button
                        type="button"
                        className="btn btn-default"
                        onClick={() => deleteIllustration(goal)}
                      >
                        <span className="glyphicon glyphicon-delete" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <Modal
        ref={curriculumModalRef}
        size="sm"
        component={
          <ZillCurriculumSelectorModal
            params={{
              type: 'CURRICULUM_ZILL',
              issuedBefore: getNow(),
              orderBy: 'issued',
              descending: true,
            }}
          />
        }
        errorBoundaryComponent={ModalErrorBoundary}
      />
      <Modal
        ref={illustrationTextModalRef}
        size="sm"
        component={<ZillIllustrationModal />}
        errorBoundaryComponent={ModalErrorBoundary}
      />
    </div>
  );
};

AsideZillIllustrations.displayName = 'AsideZillIllustrations';

export default AsideZillIllustrations;

AsideZillIllustrations.propTypes = {
  config: any,
  readonly: any,
};
